import { useReactiveVar } from "@apollo/client";
import { mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import { BetQLService } from "src/common-services";
import React, { useContext } from "react";
import TeamLogo from "src/components/Team/TeamLogo";
import { currentSport } from "src/services/ApolloService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import styles from "./styles.scss";

const { getTeamRecord, getTeamAtsRecord, getTeamOuRecord } = BetQLService;

// Input x-x-x, output: x%
const getRecordWinPercentage = (record: string) => {
  const winLossTie = record.split("-").map((x) => Number(x));

  const win = winLossTie[0];
  const ties = (winLossTie[2] ?? 0) / 2; // ties are officially half a win, half a loss

  const total = winLossTie.reduce((pre, curr) => pre + curr, 0) || 1;
  const wins = win + ties;
  const percentage = ((wins / total) * 100).toFixed(1);
  return `${percentage}%`;
};

const ScheduleSummary = ({ event }: { event: any }) => {
  const { awayTeam, homeTeam, freeGame } = event;

  const league = useReactiveVar(currentSport) ?? null;

  const homeRecord = getTeamRecord(homeTeam, league);
  const homeAtsRecord = getTeamAtsRecord(homeTeam);
  const homeOuRecord = getTeamOuRecord(homeTeam);

  const awayRecord = getTeamRecord(awayTeam, league);
  const awayAtsRecord = getTeamAtsRecord(awayTeam);
  const awayOuRecord = getTeamOuRecord(awayTeam);

  const teamDatas = [
    {
      title: "Games Played",
      homeTeam: homeTeam.teamStats.games,
      awayTeam: awayTeam.teamStats.games,
    },
    {
      title: "Record",
      homeTeam: homeRecord,
      awayTeam: awayRecord,
    },
    {
      title: "ATS Record",
      homeTeam: homeAtsRecord,
      awayTeam: awayAtsRecord,
    },
    {
      title: "Cover  %",
      homeTeam: getRecordWinPercentage(homeAtsRecord),
      awayTeam: getRecordWinPercentage(awayAtsRecord),
    },
    {
      title: "O/U Record",
      homeTeam: homeOuRecord,
      awayTeam: awayOuRecord,
    },
    {
      title: "Over  %",
      homeTeam: getRecordWinPercentage(homeOuRecord),
      awayTeam: getRecordWinPercentage(awayOuRecord),
    },
  ];

  const currentSportObj = useReactiveVar(currentSport);
  const leagueKey = currentSportObj?.key || "";
  const teamLogo = currentSportObj?.imagePath || "";
  const ctx = useContext(SubscriptionContext);
  const hasAccess = freeGame || ctx.hasSubscriptionAccess(leagueKey);

  return (
    <div className="schedule-summary__container">
      <div className="schedule-summary__title">Schedule Summary</div>
      <div className="schedule-summary__table-container">
        <div className="schedule-summary__row">
          <div className="schedule-summary__stat-data">
            <div className="schedule-summary__team-info-container">
              <TeamLogo
                containerClassName="schedule-summary__logo-container"
                className="schedule-summary__logo"
                leagueName={teamLogo}
                size={64}
                teamAbbreviation={awayTeam.preferredAbbreviation}
              />
            </div>
          </div>

          <div className="schedule-summary__stat-title" />
          <div className="schedule-summary__stat-data">
            <div className="schedule-summary__team-info-container">
              <TeamLogo
                containerClassName="schedule-summary__logo-container"
                className="schedule-summary__logo"
                leagueName={teamLogo}
                size={64}
                teamAbbreviation={homeTeam.preferredAbbreviation}
              />
            </div>
          </div>
        </div>

        <div className="schedule-summary__row">
          <div className="schedule-summary__stat-data">{awayTeam.preferredAbbreviation}</div>
          <div className="schedule-summary__stat-title">Teams</div>
          <div className="schedule-summary__stat-data">{homeTeam.preferredAbbreviation}</div>
        </div>

        {teamDatas.map((item: any, index: number) => {
          return (
            <div className="schedule-summary__row" key={index}>
              <div className="schedule-summary__stat-data">
                <span>{hasAccess ? item.awayTeam : <Icon path={mdiLock} size="16px" color="#8a8a8a" />}</span>
              </div>
              <div className="schedule-summary__stat-title">
                <span>{item.title}</span>
              </div>
              <div className="schedule-summary__stat-data">
                <span>{hasAccess ? item.homeTeam : <Icon path={mdiLock} size="16px" color="#8a8a8a" />}</span>
              </div>
            </div>
          );
        })}
      </div>
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default ScheduleSummary;
